// PRODUCTION — two-step delete confirm control
import React, { useEffect, useRef, useState } from 'react'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'

export function DeleteConfirmButton({
  onConfirm,
  resetKey,
  onPendingChange,
  disabled = false,
}: {
  onConfirm: () => void
  resetKey?: string | number
  onPendingChange?: (v: boolean) => void
  disabled?: boolean
}) {
  const [pending, setPending] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  if (disabled) {
    return <div css={tw`h-7 w-7 flex-shrink-0`} aria-hidden />
  }

  const setPendingWrapped = (v: boolean) => {
    setPending(v)
    onPendingChange?.(v)
  }

  useEffect(() => {
    setPendingWrapped(false)
  }, [resetKey])

  useEffect(() => {
    if (!pending) {
      return
    }

    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setPendingWrapped(false)
      }
    }

    document.addEventListener('mousedown', handler)

    return () => document.removeEventListener('mousedown', handler)
  }, [pending])

  if (pending) {
    return (
      <div ref={ref} css={tw`flex flex-shrink-0 items-center gap-1.5`}>
        <span css={tw`whitespace-nowrap text-[13px] font-medium text-shades-500`}>Remove?</span>
        <Button
          variant="outlined"
          size="tiny"
          color="white"
          onClick={() => setPendingWrapped(false)}
          className={tw`!border-shades-200 !text-shades-700 hover:!bg-shades-100`}
        >
          Cancel
        </Button>
        <Button
          size="tiny"
          color="primary"
          onClick={onConfirm}
          className={tw`!bg-critical-500 hover:!bg-critical-600 active:!bg-critical-600`}
        >
          Delete
        </Button>
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={() => setPendingWrapped(true)}
      css={tw`flex h-7 w-7 flex-shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-shades-400 transition-colors hover:bg-critical-100 hover:text-critical-500`}
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
  )
}
